// Configuration for the backoffice application

/**
 * Pick the backend the UI talks to, based on the hostname we're served on.
 *
 * IMPORTANT: this is client-side config, NOT a security boundary. Anyone can
 * read it in DevTools and call any backend directly — so the backend itself
 * must enforce auth, permissions, CORS and tenant checks. This only decides the
 * default backend for the UI.
 *
 * It is intentionally **fail closed**: only explicit, known production hostnames
 * get the production backend. Any unknown hostname throws instead of silently
 * defaulting to production (so a stray domain / preview host can never quietly
 * talk to prod).
 *   - localhost / LAN        → local worker
 *   - *.pages.dev            → staging backend (all Cloudflare Pages URLs)
 *   - known production hosts → production backend
 *   - anything else          → throw
 */
const PROD_HOSTS = new Set([
  'backoffice.mvingenieursbureau.nl', // current production domain
  'geovoice.nl',                      // future production domain
  'www.geovoice.nl',
  'app.geovoice.nl',
]);

function detectApiBase() {
  const h = (typeof window !== 'undefined' && window.location?.hostname) || '';

  if (h === 'localhost' || h === '127.0.0.1' || h.startsWith('192.168.') || h.endsWith('.local')) {
    return 'http://localhost:8788/api';
  }
  if (h.endsWith('.pages.dev')) {
    return 'https://geovoice-backend-staging.late-river-1da9.workers.dev/api';
  }
  if (PROD_HOSTS.has(h)) {
    return 'https://terra-voice-backend.late-river-1da9.workers.dev/api';
  }
  throw new Error(
    `Unknown frontend hostname "${h}" — refusing to pick a backend. ` +
    `Add it to PROD_HOSTS in config.js if it is a production domain.`
  );
}

/**
 * Which environment the UI is served on, by the same hostname logic as the
 * backend selection. Used to show a non-production badge (so you always know
 * you're not on prod). 'development' = localhost/LAN, 'staging' = *.pages.dev.
 */
function detectEnv() {
  const h = (typeof window !== 'undefined' && window.location?.hostname) || '';
  if (h === 'localhost' || h === '127.0.0.1' || h.startsWith('192.168.') || h.endsWith('.local')) return 'development';
  if (h.endsWith('.pages.dev')) return 'staging';
  if (PROD_HOSTS.has(h)) return 'production';
  return 'unknown';
}

const ENV_LABELS = { development: 'LOCAL', staging: 'STAGING', production: 'PRODUCTIE', unknown: 'ONBEKEND' };

// VITE_API_BASE override is only honoured during local dev (`vite dev`), never
// in a built bundle — so a stray VITE_API_BASE in the Cloudflare dashboard can
// never repoint production or preview to the wrong backend.
const envApiBase = import.meta.env.DEV ? import.meta.env.VITE_API_BASE : undefined;

const ENV = detectEnv();

export const CONFIG = {
  API_BASE: envApiBase || detectApiBase(),

  // Environment (for the non-production badge under the logo).
  ENV,
  ENV_LABEL: ENV_LABELS[ENV] || ENV_LABELS.unknown,
  IS_PRODUCTION: ENV === 'production',
  // Git branch, injected at build/dev time (see vite.config). Empty in production builds.
  BRANCH: (import.meta.env.VITE_GIT_BRANCH || '').trim(),

  // Polling interval for status updates (in milliseconds)
  POLLING_INTERVAL: 2000,

  // Maximum file size for uploads (in bytes) - 50MB
  MAX_FILE_SIZE: 50 * 1024 * 1024,

  // Accepted file types for upload
  ACCEPTED_FILE_TYPES: [
    'audio/mpeg',
    'audio/mp4',
    'audio/wav',
    'audio/webm',
    'audio/m4a',
    'text/plain',
    'application/pdf'
  ]
};
